import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dropdown, Tooltip } from 'antd';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { ChevronDown, Plus, ShieldAlert, Snowflake } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import { meName } from '../CustomerDemo/softphone';
import {
  DOC_STATUS, DOC_TYPE, ITEM_KIND, daysToExpiry, expiryLevel, inTransit, type DocType, type Item, type StockDoc,
} from './khoLogic';

export { PageHeader, Kpi, Panel } from '../CskhHub/shared';

export const docUrl = (id: string) => `/import-export/detail?id=${id}`;
export const itemUrl = (id: string, extra = '') => `/warehouses/detail?id=${id}${extra}`;
export const newDocUrl = (type: DocType, extra = '') =>
  type === 'import' ? `/import-export/add-import${extra ? `?${extra}` : ''}`
    : type === 'count' ? `/import-export/create-inventory-count${extra ? `?${extra}` : ''}`
      : `/import-export/add-export?type=${type}${extra ? `&${extra}` : ''}`;

/** Quyền thao tác kho (module warehouse) */
export function useKhoPerms() {
  const { isSuperAdmin, hasPermission } = useAuthStore();
  const can = (a: string) => isSuperAdmin || hasPermission(`warehouse.${a}`);
  return { canCreate: can('create'), canEdit: can('edit'), canApprove: can('approve'), canExport: can('export'), me: meName() || 'Tôi' };
}

export const Badge = ({ className, children, title }: { className: string; children: React.ReactNode; title?: string }) => (
  <span title={title} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-medium whitespace-nowrap ${className}`}>{children}</span>
);

export const DocTypeTag = ({ type }: { type: DocType }) => <Badge className={DOC_TYPE[type].badge}>{DOC_TYPE[type].label}</Badge>;

export function DocStatusTag({ doc }: { doc: StockDoc }) {
  if (inTransit(doc)) return <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200">Đang chuyển</Badge>;
  return <Badge className={DOC_STATUS[doc.status].badge}>{DOC_STATUS[doc.status].label}</Badge>;
}

export const KindTag = ({ item }: { item: Item }) => <Badge className={ITEM_KIND[item.kind].badge}>{ITEM_KIND[item.kind].label}</Badge>;

/** Biểu tượng bảo quản lạnh / thuốc kiểm soát */
export const ItemFlags = ({ item }: { item: Item | null }) => (
  <>
    {item?.cold && <Tooltip title="Bảo quản lạnh 2–8°C"><Snowflake size={13} className="inline text-sky-500 ml-1" /></Tooltip>}
    {item?.controlled && <Tooltip title="Thuốc kiểm soát — cần duyệt chuyên môn khi xuất"><ShieldAlert size={13} className="inline text-violet-600 ml-1" /></Tooltip>}
  </>
);

export function ItemCell({ item, sub }: { item: Item | null; sub?: React.ReactNode }) {
  const navigate = useNavigate();
  if (!item) return <span className="text-gray-400">Mặt hàng đã xóa</span>;
  return (
    <div className="min-w-0">
      <button type="button" onClick={e => { e.stopPropagation(); navigate(itemUrl(item.id)); }}
        className="font-medium text-gray-900 dark:text-gray-100 hover:text-[#3e91ab] text-left">
        {item.name}
      </button>
      <ItemFlags item={item} />
      <div className="text-[11px] text-gray-400">{item.code}{sub ? <> · {sub}</> : null}</div>
    </div>
  );
}

const EXPIRY_CLS = {
  expired: 'text-rose-600 font-semibold',
  urgent: 'text-orange-600 font-semibold',
  near: 'text-amber-600',
  ok: 'text-gray-600 dark:text-gray-300',
  none: 'text-gray-300',
};

export function ExpiryText({ expiry, now, short }: { expiry?: string; now: number; short?: boolean }) {
  const lv = expiryLevel(expiry, now);
  if (lv === 'none' || !expiry) return <span className={EXPIRY_CLS.none}>—</span>;
  const d = daysToExpiry(expiry, now)!;
  const hint = d < 0 ? `hết hạn ${-d} ngày` : `còn ${d} ngày`;
  return (
    <span className={`whitespace-nowrap ${EXPIRY_CLS[lv]}`}>
      {dayjs(expiry).format('DD/MM/YYYY')}{(!short || lv !== 'ok') && <span className="text-[11px] ml-1">({hint})</span>}
    </span>
  );
}

const CREATE_TYPES: DocType[] = ['import', 'issue_service', 'issue_sale', 'transfer', 'dispose', 'count'];

/** Nút "Tạo phiếu" chung cho các màn kho */
export function CreateDocButton({ types = CREATE_TYPES, label = 'Tạo phiếu' }: { types?: DocType[]; label?: string }) {
  const navigate = useNavigate();
  const { canCreate } = useKhoPerms();
  if (!canCreate) return null;
  return (
    <Dropdown trigger={['click']} menu={{ items: types.map(t => ({ key: t, label: DOC_TYPE[t].label })), onClick: e => navigate(newDocUrl(e.key as DocType)) }}>
      <Button type="primary" icon={<Plus size={14} />}>{label} <ChevronDown size={14} /></Button>
    </Dropdown>
  );
}

export function exportXlsx(fileName: string, sheet: string, rows: Record<string, string | number>[]) {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheet.slice(0, 31));
  XLSX.writeFile(wb, `${fileName}_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
}

export const fmtDate = (t: number) => dayjs(t).format('DD/MM/YYYY');
export const fmtDateTime = (t: number) => dayjs(t).format('HH:mm DD/MM/YYYY');

/** Bộ chọn dạng "chip" (lọc nhanh) */
export function Chip({ active, onClick, children, tone = 'gray' }: { active: boolean; onClick: () => void; children: React.ReactNode; tone?: 'gray' | 'rose' | 'amber' | 'sky' | 'violet' }) {
  const tones = {
    gray: active ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400',
    rose: active ? 'bg-rose-600 text-white border-rose-600' : 'bg-rose-50 text-rose-700 border-rose-200 hover:border-rose-400',
    amber: active ? 'bg-amber-500 text-white border-amber-500' : 'bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-400',
    sky: active ? 'bg-sky-600 text-white border-sky-600' : 'bg-sky-50 text-sky-700 border-sky-200 hover:border-sky-400',
    violet: active ? 'bg-violet-600 text-white border-violet-600' : 'bg-violet-50 text-violet-700 border-violet-200 hover:border-violet-400',
  };
  return (
    <button type="button" onClick={onClick} className={`px-3 py-1 rounded-full border text-xs font-medium transition ${tones[tone]}`}>{children}</button>
  );
}
